/**
 * Edit User Modal
 * Form to edit an existing organization member
 */

import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { MultiSelect } from '../../components/shared/MultiSelect/MultiSelect';
import userService, { Member, UpdateMemberRequest } from '../../services/api/userService';
import roleService, { Role } from '../../services/api/roleService';
import { User } from 'lucide-react';

interface EditUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: Member;
}

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const ErrorMessage = styled.div`
  padding: ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.status.error.bg};
  border: 1px solid ${({ theme }) => theme.colors.status.error.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.status.error.text};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const Select = styled.select`
  width: 100%;
  min-height: 44px;
  padding: 0 ${({ theme }) => theme.spacing[3]};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => theme.colors.backgrounds.white};
  transition: ${({ theme }) => theme.transitions.fast};

  &:hover {
    border-color: ${({ theme }) => theme.colors.borders.medium};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary.base};
    box-shadow: ${({ theme }) => theme.shadows.ring};
  }
`;

const Label = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  display: block;
`;

export const EditUserModal = ({ isOpen, onClose, user }: EditUserModalProps) => {
  const queryClient = useQueryClient();
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>(
    user.roles?.map((r) => r.id) ?? [],
  );

  useEffect(() => {
    setSelectedRoleIds(user.roles?.map((r) => r.id) ?? []);
  }, [user]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<UpdateMemberRequest>({
    defaultValues: {
      firstName: user.firstName,
      lastName: user.lastName,
      status: user.status,
    },
  });

  const { data: rolesData } = useQuery({
    queryKey: ['roles'],
    queryFn: () => roleService.getRoles({ limit: 50 }),
    enabled: isOpen,
  });

  const updateMutation = useMutation({
    mutationFn: (data: UpdateMemberRequest) => userService.updateMember(user.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['members'] });
      onClose();
    },
  });

  const onSubmit = (data: UpdateMemberRequest) => {
    updateMutation.mutate({
      ...data,
      roleIds: selectedRoleIds,
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit User"
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary-blue"
            onClick={handleSubmit(onSubmit)}
            isLoading={updateMutation.isPending}
          >
            Save Changes
          </Button>
        </>
      }
    >
      <Form onSubmit={handleSubmit(onSubmit)}>
        {updateMutation.isError && (
          <ErrorMessage>
            {(updateMutation.error as any)?.response?.data?.message ||
              'Failed to update user. Please try again.'}
          </ErrorMessage>
        )}

        <Input
          type="email"
          label="Email"
          value={user.email}
          disabled
          fullWidth
          helperText="Email cannot be changed"
        />

        <Input
          {...register('firstName', {
            required: 'First name is required',
          })}
          type="text"
          label="First Name"
          placeholder="John"
          leftIcon={<User />}
          error={errors.firstName?.message}
          fullWidth
        />

        <Input
          {...register('lastName', {
            required: 'Last name is required',
          })}
          type="text"
          label="Last Name"
          placeholder="Doe"
          leftIcon={<User />}
          error={errors.lastName?.message}
          fullWidth
        />

        <div>
          <Label>Status</Label>
          <Select {...register('status')}>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </Select>
        </div>

        <div>
          <Label>Roles</Label>
          <MultiSelect<Role>
            items={rolesData?.data ?? []}
            selectedIds={selectedRoleIds}
            onChange={setSelectedRoleIds}
            getItemId={(role) => role.id}
            getItemLabel={(role) => role.name}
            placeholder="Select roles..."
            searchable={false}
          />
        </div>
      </Form>
    </Modal>
  );
};
